/**
 * Montagekalender – der Monat, in dem die Montagen stehen.
 *
 * Zwei Ansichten aus einer Datei (MONTAGEKALENDER.md 2 und 6): die
 * Büro-Ansicht am Arbeitsplatz und die Bildschirm-Ansicht für den Legamaster
 * im Büro. Dieselben Daten, dieselbe Monatslogik – nur größer, ohne Menü und
 * ohne alles, was etwas verändert. Zwei Dateien wären zwei Kalender, die
 * irgendwann verschieden rechnen.
 *
 * Quelle ist der Outlook-Kalender „MONTAGE - Termine". Was Kora eingetragen
 * hat, lässt sich über den Stift verschieben und löschen; was jemand direkt
 * in Outlook angelegt hat, wird angezeigt und nie angefasst (1.3). Ein Klick
 * auf einen Eintrag öffnet den Termin in Outlook – angesehen wird er dort,
 * nicht hier.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { Nachrichtenknopf } from '../komponenten/ChatKontext';
import { KoraMaskenhinweise } from '../komponenten/KoraHinweise';
import { Menueknopf } from '../komponenten/Shell';
import { useKora, useMeta } from '../zustand';
import { MontageDialog } from '../modals/MontageDialog';
import type { MontageAntwort, MontageEinsatz } from '../typen';
import './montage.css';

/* ---- Datum ---------------------------------------------------------------- */

const MONATE = [
  'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WOCHENTAGE_LANG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const zwei = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
const ausIso = (s: string) => new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));

function plusTage(s: string, n: number): string {
  const d = ausIso(s);
  d.setDate(d.getDate() + n);
  return iso(d);
}

/** Die ISO-Kalenderwoche – die Woche des Donnerstags, wie im Kalender in Mein Tag. */
function kalenderwoche(s: string): number {
  const d = ausIso(s);
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const tag = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - tag);
  const jahresbeginn = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - jahresbeginn.getTime()) / 86400000 + 1) / 7);
}

const istWochenende = (s: string) => [0, 6].includes(ausIso(s).getDay());
const lang = (s: string) => {
  const d = ausIso(s);
  return `${WOCHENTAGE_LANG[(d.getDay() + 6) % 7]}, ${d.getDate()}. ${MONATE[d.getMonth()]}`;
};
const mittel = (s: string) => {
  const d = ausIso(s);
  return `${WOCHENTAGE[(d.getDay() + 6) % 7]} ${d.getDate()}. ${MONATE[d.getMonth()]}`;
};

/** Die Wochen eines Monats, Montag bis Sonntag, mit den Rändern der Nachbarmonate. */
function wochenVon(jahr: number, monat: number): string[][] {
  const erster = new Date(jahr, monat, 1);
  const start = new Date(erster);
  start.setDate(1 - ((erster.getDay() + 6) % 7));
  const wochen: string[][] = [];
  const laufend = new Date(start);
  for (let w = 0; w < 6; w++) {
    const tage: string[] = [];
    for (let t = 0; t < 7; t++) {
      tage.push(iso(laufend));
      laufend.setDate(laufend.getDate() + 1);
    }
    wochen.push(tage);
    /* Wochen, in denen kein Tag des Monats mehr steht, entfallen. */
    if (laufend.getMonth() !== monat && laufend > new Date(jahr, monat + 1, 0)) break;
  }
  return wochen;
}

/* ---- Farben ---------------------------------------------------------------- */

/**
 * Zwei Arten, zwei Töne – beide aus v14 Kapitel 2 (2.4).
 *
 * Montage trägt Warmstein wie die Rubrik „Lieferung & Montage", Service das
 * Olive der Rubrik Service – dasselbe Olive, das die Aufträge-Liste für die
 * Phase Service benutzt. Ein Service sieht in Kora überall gleich aus.
 * Gelb gibt es nur für die Auswahl.
 */
const ART: Record<string, { label: string; akz: string; txt: string; tint: string }> = {
  montage: { label: 'Montage', akz: 'var(--eh-ton-stone)', txt: 'var(--eh-ton-stone-dunkel)', tint: 'var(--eh-ton-stone-tint)' },
  service: { label: 'Service', akz: 'var(--eh-ton-olive)', txt: 'var(--eh-ton-olive-dunkel)', tint: 'var(--eh-ton-olive-tint)' },
};

/*
 * Die Farbe sagt, was es ist – nicht, woher es kommt.
 *
 * Bis hierher bekam jeder direkt in Outlook angelegte Termin eine eigene
 * Farbe. Damit war der Kalender voll mit der Auskunft „stammt nicht aus
 * Kora", und die Montage war von einem Service nicht zu unterscheiden.
 * Auf Dauer entstehen die Termine ohnehin nur noch in Kora.
 */
const artVon = (e: MontageEinsatz) => ART[e.art] ?? ART.montage!;

/**
 * Wohin der Klick auf einen Termin führt (MONTAGEKALENDER.md 11.5).
 *
 * Erste Wahl ist das Outlook auf dem PC: `outlook:{EntryID}` öffnet den
 * Termin dort, ohne Umweg über den Browser. Das setzt zweierlei voraus –
 * einen registrierten `outlook:`-Handler und das klassische Outlook mit
 * Tibors Kalender. Fehlt eines davon, bleibt der Web-Link; er tut immer
 * etwas, und das ist besser als ein Klick, der ins Leere geht.
 */
function terminZiel(e: MontageEinsatz, amPc: boolean): { href: string; amPc: boolean } | null {
  if (amPc && e.entryId) return { href: `outlook:${e.entryId}`, amPc: true };
  if (e.webLink) return { href: e.webLink, amPc: false };
  return null;
}

/** Liegt ein Einsatz an diesem Tag? Mehrtägige stehen an jedem Tag (2.3). */
const amTag = (e: MontageEinsatz, tag: string) => e.von <= tag && e.bis >= tag;

/**
 * Ist ein Monteur an diesen Tagen schon woanders (2.4, 3.4)?
 *
 * Doppelbelegung wird gezeigt, nicht verhindert: Manchmal ist sie gewollt –
 * zwei Baustellen am selben Tag, eine davon halb. Zu blockieren hieße, es
 * besser zu wissen als der, der plant.
 */
function doppelt(e: MontageEinsatz, alle: MontageEinsatz[]): string[] {
  const namen: string[] = [];
  for (const m of e.monteure) {
    const woanders = alle.find(
      (x) => x.id !== e.id && x.monteure.some((y) => y.id === m.id) && x.von <= e.bis && x.bis >= e.von,
    );
    if (woanders) namen.push(`${m.name} zugleich bei ${woanders.kurz || woanders.titel}`);
  }
  return namen;
}

/* ---- Seite ----------------------------------------------------------------- */

export function Montagekalender() {
  const meta = useMeta();
  const { nutzer } = useKora();
  const ort = useLocation();
  const queryClient = useQueryClient();

  const heute = meta.heute;
  /*
   * Ein Anzeige-Konto landet immer auf dem Bildschirm (6.1); alle anderen
   * können ihn über `?anzeige=1` zum Prüfen aufrufen (6.5).
   */
  const anzeigeErzwungen = new URLSearchParams(ort.search).get('anzeige') === '1';
  const nurAnzeige = !!nutzer?.nurAnzeige;
  const [bildschirm, setBildschirm] = useState(nurAnzeige || anzeigeErzwungen);

  const [jahr, setJahr] = useState(() => Number(heute.slice(0, 4)));
  const [monat, setMonat] = useState(() => Number(heute.slice(5, 7)) - 1);
  const [gewaehlt, setGewaehlt] = useState(heute);
  /*
   * Gefiltert wird nach Monteur, nicht nach Art.
   *
   * „Montage oder Service" beantwortet die Farbe schon. Die Frage am
   * Schreibtisch ist eine andere: Was hat Mirek diesen Monat? `alle` oder
   * die Id eines Monteurs.
   */
  const [filter, setFilter] = useState<string>('alle');
  const [dialog, setDialog] = useState<{ einsatz: MontageEinsatz | null; angebotId: number | null; tag?: string } | null>(null);

  const wochen = useMemo(() => wochenVon(jahr, monat), [jahr, monat]);
  const von = wochen[0]![0]!;
  const bis = wochen[wochen.length - 1]![6]!;

  const abfrage = useQuery({
    queryKey: ['montage', von, bis],
    queryFn: () => api.hole<MontageAntwort>(`/montage?von=${von}&bis=${bis}`),
    /* Der Bildschirm liest alle fünf Minuten nach (6.4). */
    refetchInterval: bildschirm ? 5 * 60 * 1000 : false,
  });
  const daten = abfrage.data;
  const einsaetze = useMemo(() => daten?.einsaetze ?? [], [daten]);


  /* ---- Bildschirm: Uhr und Rückkehr auf heute (6.4) ---------------------- */

  const [jetzt, setJetzt] = useState(() => new Date());
  const letzteBeruehrung = useRef(Date.now());

  useEffect(() => {
    if (!bildschirm) return;
    const uhr = setInterval(() => setJetzt(new Date()), 30_000);
    /*
     * Zwei Minuten ohne Berührung: zurück auf heute und den laufenden Monat.
     * Sonst steht das Gerät am nächsten Morgen noch im Oktober, weil abends
     * jemand geblättert hat.
     */
    const rueckkehr = setInterval(() => {
      if (Date.now() - letzteBeruehrung.current < 2 * 60 * 1000) return;
      setJahr(Number(heute.slice(0, 4)));
      setMonat(Number(heute.slice(5, 7)) - 1);
      setGewaehlt(heute);
    }, 15_000);
    return () => {
      clearInterval(uhr);
      clearInterval(rueckkehr);
    };
  }, [bildschirm, heute]);

  const beruehrt = () => {
    letzteBeruehrung.current = Date.now();
  };

  /* ---- Gefilterte Menge --------------------------------------------------- */

  const sichtbar = useMemo(() => {
    if (filter === 'alle') return einsaetze;
    return einsaetze.filter((e) => e.monteure.some((m) => m.id === filter));
  }, [einsaetze, filter]);

  const imMonat = (e: MontageEinsatz) => e.bis >= `${jahr}-${zwei(monat + 1)}-01` && e.von <= bis;
  const zaehle = (wen: string) => {
    if (wen === 'alle') return einsaetze.filter(imMonat).length;
    return einsaetze.filter((e) => imMonat(e) && e.monteure.some((m) => m.id === wen)).length;
  };

  const desTages = sichtbar.filter((e) => amTag(e, gewaehlt));

  function monatSetzen(schritt: number) {
    beruehrt();
    const d = new Date(jahr, monat + schritt, 1);
    setJahr(d.getFullYear());
    setMonat(d.getMonth());
  }

  function aufHeute() {
    beruehrt();
    setJahr(Number(heute.slice(0, 4)));
    setMonat(Number(heute.slice(5, 7)) - 1);
    setGewaehlt(heute);
  }

  const outlookStand =
    daten?.outlook.stand === 'ok'
      ? `Outlook „${daten.kalenderName}" · aktualisiert ${new Date(daten.outlook.gelesenUm).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}`
      : daten?.outlook.stand === 'aus'
        ? 'Outlook ist in dieser Umgebung abgeschaltet'
        : `Outlook antwortet nicht${daten?.outlook.meldung ? ` · ${daten.outlook.meldung}` : ''}`;

  /* ---- Bildschirm-Ansicht -------------------------------------------------- */

  if (bildschirm) {
    const naechste: string[] = [];
    for (let i = 1; naechste.length < 5 && i < 21; i++) {
      const tag = plusTage(heute, i);
      if (!istWochenende(tag)) naechste.push(tag);
    }

    return (
      <div className="mk-screen" onPointerDown={beruehrt}>
        <div className="mk-screen-kopf">
          <div className="mk-screen-marke">
            <img src="/eichenherz-logo.png" alt="Eichenherz" className="mk-screen-logo" />
            <span className="mk-screen-strich" />
            <div>
              <div className="mk-screen-titel">Montagekalender</div>
              <div className="mk-screen-unter">
                {MONATE[monat]} {jahr} · heute KW {kalenderwoche(heute)}
              </div>
            </div>
          </div>
          <div className="mk-screen-rechts">
            <div>
              <div className="mk-screen-datum">{lang(heute)}</div>
              <div className={`mk-screen-stand ${daten?.outlook.stand === 'ok' ? '' : 'offline'}`}>
                <span className="mk-screen-punkt" />
                {outlookStand}
              </div>
            </div>
            <div className="mk-screen-uhr">
              {zwei(jetzt.getHours())}:{zwei(jetzt.getMinutes())}
            </div>
          </div>
        </div>

        <div className="mk-screen-raster">
          <div className="mk-screen-karte">
            <div className="mk-screen-blaettern">
              <button type="button" className="mk-screen-knopf" onClick={() => monatSetzen(-1)} aria-label="Monat zurück">
                ‹
              </button>
              <span className="mk-screen-monat">
                {MONATE[monat]} {jahr}
              </span>
              <button type="button" className="mk-screen-knopf" onClick={() => monatSetzen(1)} aria-label="Monat vor">
                ›
              </button>
              <button type="button" className="mk-screen-knopf mk-screen-knopf--weit" onClick={aufHeute}>
                Heute
              </button>
              <span className="mk-screen-hinweis">Tag antippen zeigt ihn rechts · nach 2 Min. zurück zu heute</span>
            </div>
            <Monatsraster
              wochen={wochen}
              monat={monat}
              heute={heute}
              gewaehlt={gewaehlt}
              einsaetze={sichtbar}
              alle={einsaetze}
              gross
              onTag={(t) => {
                beruehrt();
                setGewaehlt(t);
              }}
            />
          </div>

          <div className="mk-screen-spalte">
            <div className="mk-screen-karte mk-screen-tag">
              <div className="mk-screen-kartenkopf">
                <span>{mittel(gewaehlt).toUpperCase()}{gewaehlt === heute ? ' · HEUTE' : ''}</span>
                <span>
                  KW {kalenderwoche(gewaehlt)} · {desTages.length} {desTages.length === 1 ? 'Einsatz' : 'Einsätze'}
                </span>
              </div>
              {desTages.length === 0 && <div className="mk-screen-leer">Kein Einsatz an diesem Tag.</div>}
              {desTages.map((e) => (
                <div key={e.id} className="mk-screen-einsatz" style={{ borderLeftColor: artVon(e).akz, background: artVon(e).tint }}>
                  <div className="mk-screen-einsatz-titel">{e.titel}</div>
                  <div className="mk-screen-einsatz-zeit">{e.zeitraum}</div>
                  {e.adresse && <div className="mk-screen-einsatz-ort">{e.adresse}</div>}
                  <div className="mk-screen-chips">
                    {e.monteure.map((m) => (
                      <span key={m.id} className="mk-screen-chip">
                        <span className="mk-screen-avatar">{m.kuerzel}</span>
                        {m.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mk-screen-karte">
              <div className="mk-screen-kartenkopf">
                <span>DIE NÄCHSTEN ARBEITSTAGE</span>
              </div>
              {naechste.map((tag) => {
                const drin = sichtbar.filter((e) => amTag(e, tag));
                return (
                  <div key={tag} className="mk-screen-naechster">
                    <div className="mk-screen-naechster-tag">{mittel(tag)}</div>
                    {drin.length === 0 && <div className="mk-screen-frei">frei</div>}
                    {drin.map((e) => (
                      <div key={e.id} className="mk-screen-zeile" style={{ borderLeftColor: artVon(e).akz }}>
                        <span className="mk-screen-zeile-titel">{e.titel}</span>
                        <span className="mk-screen-zeile-wer">{e.monteure.map((m) => m.name.split(' ')[0]).join(', ')}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mk-screen-fuss">
          <Legende gross />
          {!nurAnzeige && (
            <button type="button" className="mk-screen-zurueck" onClick={() => setBildschirm(false)}>
              zurück zur Büro-Ansicht
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ---- Büro-Ansicht -------------------------------------------------------- */

  return (
    <div className="mk">
      <div className="mk-kopf">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div>
            <h1 className="mk-titel">Montagekalender</h1>
            <div className="mk-untertitel">
              {daten
                ? `${einsaetze.length} ${einsaetze.length === 1 ? 'Einsatz' : 'Einsätze'} · ${einsaetze.filter((e) => e.art === 'service').length} davon Service`
                : 'wird geladen …'}
            </div>
          </div>
        </div>
        <div className="eh-kopf-rechts">
          <span className={`mk-stand ${daten?.outlook.stand === 'ok' ? '' : 'offline'}`}>
            <span className="mk-stand-punkt" />
            {outlookStand}
          </span>
          <button type="button" className="mk-sek" onClick={() => setBildschirm(true)}>
            Bildschirm-Ansicht
          </button>
          <Nachrichtenknopf />
          <button type="button" className="mk-primaer" onClick={() => setDialog({ einsatz: null, angebotId: null })}>
            + Montage eintragen
          </button>
        </div>
      </div>

      {abfrage.isLoading && <div className="eh-karte">Der Kalender wird geladen …</div>}
      {abfrage.isError && <div className="eh-karte">Der Kalender konnte nicht geladen werden.</div>}

      {daten && (
        <div className="mk-raster">
          <div className="mk-spalte">
            <div className="mk-leiste">
              <div className="mk-blaettern">
                <button type="button" className="mk-rund" onClick={() => monatSetzen(-1)} aria-label="Monat zurück">
                  ‹
                </button>
                <span className="mk-monat">
                  {MONATE[monat]} {jahr}
                </span>
                <button type="button" className="mk-rund" onClick={() => monatSetzen(1)} aria-label="Monat vor">
                  ›
                </button>
                <button type="button" className="mk-tertiaer" onClick={aufHeute}>
                  Heute
                </button>
              </div>
              {/* Wer ist eingeteilt – eine Pille je Monteur (Kapitel 2.5). */}
              <div className="mk-filter">
                <button
                  type="button"
                  className={`mk-pille ${filter === 'alle' ? 'aktiv' : ''}`}
                  onClick={() => setFilter('alle')}
                >
                  Alle
                  <span className="mk-pille-zahl">{zaehle('alle')}</span>
                </button>
                {daten.monteure.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`mk-pille ${filter === m.id ? 'aktiv' : ''}`}
                    title={m.zusatz ? `${m.name} · ${m.zusatz}` : m.name}
                    onClick={() => setFilter(filter === m.id ? 'alle' : m.id)}
                  >
                    <span className="mk-avatar mk-pille-avatar">{m.kuerzel}</span>
                    {m.name.split(' ')[0]}
                    <span className="mk-pille-zahl">{zaehle(m.id)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mk-kalender">
              <Monatsraster
                wochen={wochen}
                monat={monat}
                heute={heute}
                gewaehlt={gewaehlt}
                einsaetze={sichtbar}
                alle={einsaetze}
                outlookAmPc={!!nutzer?.outlookAmPc}
                /*
                  Klick auf die freie Fläche eines Tages trägt eine Montage
                  ein (11.3) – die Tageskarte, die er früher füllte, gibt es
                  nicht mehr. Termine selbst öffnen in Outlook.
                */
                onTag={(t) => setDialog({ einsatz: null, angebotId: null, tag: t })}
                onBearbeiten={(e) => setDialog({ einsatz: e, angebotId: e.angebotId })}
              />
            </div>

            <Legende />
          </div>

          <div className="mk-seite">
            {/* Auslastung (2.6, zweite Karte). */}
            <div className="mk-karte">
              <div className="mk-kartenkopf">
                <span className="mk-kicker">
                  Auslastung · {MONATE[monat]!.slice(0, 3)}. {jahr}
                </span>
                <span className="mk-zusatz">{daten.auslastung[0]?.arbeitstage ?? 0} Arbeitstage</span>
              </div>
              {/*
                Hat im Monat niemand einen Einsatz, steht ein Satz da und
                nicht acht Mal „0 / 25" (11.4). Eine Reihe aus Nullen sieht
                aus wie ein Fehler und sagt nichts.
              */}
              {daten.auslastung.every((m) => m.belegt === 0) ? (
                <span className="mk-leer">In diesem Monat ist noch kein Monteur eingeteilt.</span>
              ) : (
                daten.auslastung.map((m) => {
                const anteil = m.arbeitstage ? Math.round((m.belegt / m.arbeitstage) * 100) : 0;
                return (
                  <div key={m.id} className="mk-last">
                    <span className="mk-avatar">{m.kuerzel}</span>
                    <span className="mk-last-name">
                      {m.name}
                      {m.zusatz ? <span className="mk-last-zusatz"> · {m.zusatz}</span> : null}
                    </span>
                    <span className="mk-last-zahl">
                      {m.belegt} / {m.arbeitstage}
                    </span>
                    <span className="mk-last-spur">
                      <span
                        className={`mk-last-balken ${anteil >= 85 ? 'voll' : ''}`}
                        style={{ width: `${Math.min(100, anteil)}%` }}
                      />
                    </span>
                  </div>
                );
                })
              )}
            </div>

            {/* Noch ohne Montagetermin (2.6, dritte Karte). */}
            <div className="mk-karte">
              <div className="mk-kartenkopf">
                <span className="mk-kicker">Noch ohne Montagetermin</span>
                <span className="mk-zusatz">
                  {daten.ohneTermin.length} {daten.ohneTermin.length === 1 ? 'Auftrag' : 'Aufträge'}
                </span>
              </div>
              {daten.ohneTermin.length === 0 && <span className="mk-leer">Alles verplant.</span>}
              {daten.ohneTermin.map((a) => (
                <div key={a.angebotId} className="mk-offen">
                  <span className="mk-offen-text">
                    <span className="mk-offen-nr">{a.name}</span>
                    <span className={`mk-offen-hinweis ${a.dringend ? 'warnt' : ''}`}>{a.hinweis}</span>
                  </span>
                  <button
                    type="button"
                    className="mk-primaer mk-primaer--klein"
                    onClick={() => setDialog({ einsatz: null, angebotId: a.angebotId })}
                  >
                    Montage planen
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {dialog && daten && (
        <MontageDialog
          einsatz={dialog.einsatz}
          angebotId={dialog.angebotId}
          vorgabeTag={dialog.tag}
          monteure={daten.monteure}
          alleEinsaetze={einsaetze}
          onSchliessen={() => setDialog(null)}
          onFertig={() => {
            setDialog(null);
            void queryClient.invalidateQueries({ queryKey: ['montage'] });
            void queryClient.invalidateQueries({ queryKey: ['abwicklung'] });
            void queryClient.invalidateQueries({ queryKey: ['mein-tag'] });
          }}
        />
      )}

      {/* Kora steht unter dem Inhalt der Maske, nicht darüber (v14, 10.1). */}
      <KoraMaskenhinweise maske="montage" label="Montagekalender" />
    </div>
  );
}

/* ---- Bausteine -------------------------------------------------------------- */

function Legende({ gross = false }: { gross?: boolean }) {
  return (
    <div className={`mk-legende ${gross ? 'gross' : ''}`}>
      {[
        [ART.montage!, 'Montage'],
        [ART.service!, 'Service-Einsatz'],
      ].map(([a, text]) => (
        <span key={text as string} className="mk-legende-punkt">
          <span
            className="mk-legende-feld"
            style={{ background: (a as { tint: string }).tint, borderLeftColor: (a as { akz: string }).akz }}
          />
          {text as string}
        </span>
      ))}
      <span className="mk-legende-punkt">
        <span className="mk-legende-feld mk-legende-feld--doppelt" />
        Monteur doppelt verplant
      </span>
    </div>
  );
}

function Monatsraster({
  wochen,
  monat,
  heute,
  gewaehlt,
  einsaetze,
  alle,
  gross = false,
  outlookAmPc = false,
  onTag,
  onBearbeiten,
}: {
  wochen: string[][];
  monat: number;
  heute: string;
  gewaehlt: string;
  einsaetze: MontageEinsatz[];
  alle: MontageEinsatz[];
  gross?: boolean;
  /** Öffnet der Nutzer Termine im Outlook am PC? (11.5) */
  outlookAmPc?: boolean;
  /** Büro: Klick auf die freie Fläche eines Tages. An der Wand: Tageswahl. */
  onTag: (tag: string) => void;
  /* Fehlt an der Wand – dort wird nichts bearbeitet. */
  onBearbeiten?: (e: MontageEinsatz) => void;
}) {
  return (
    <div className={`mk-monat ${gross ? 'gross' : ''}`}>
      <div className="mk-monat-kopf">
        <span className="mk-kw">KW</span>
        {/*
          Nur Montag bis Freitag. Am Wochenende wird nicht montiert, und
          zwei leere Spalten nehmen den fünf vollen die Breite weg. Die
          Woche wird weiterhin mit sieben Tagen gerechnet, damit die
          Kalenderwoche und mehrtägige Einsätze stimmen.
        */}
        {WOCHENTAGE.slice(0, 5).map((w) => (
          <span key={w} className="mk-wt">
            {w}
          </span>
        ))}
      </div>
      {wochen.map((woche) => (
        <div key={woche[0]} className="mk-woche">
          <span className="mk-kw-zahl">{kalenderwoche(woche[0]!)}</span>
          {woche.slice(0, 5).map((tag) => {
            const drin = einsaetze.filter((e) => amTag(e, tag));
            const fremd = ausIso(tag).getMonth() !== monat;
            return (
              <div
                key={tag}
                className={`mk-tag ${tag === heute ? 'heute' : ''} ${fremd ? 'fremd' : ''} ${gewaehlt === tag ? 'gewaehlt' : ''}`}
              >
                {/*
                  Die Fläche wählt den Tag, die Einträge öffnen ihren Termin
                  in Outlook. Als Knopf im Knopf ginge das nicht – also liegt
                  die Tagesfläche als eigenes Feld hinter den Einträgen.
                */}
                {!gross && (
                  <button
                    type="button"
                    className="mk-tag-flaeche"
                    onClick={() => onTag(tag)}
                    aria-label={`${mittel(tag)} wählen`}
                  />
                )}
                <span className="mk-tag-kopf">
                  <span className={`mk-tag-zahl ${tag === heute ? 'heute' : ''} ${gewaehlt === tag ? 'aktiv' : ''}`}>
                    {ausIso(tag).getDate()}
                  </span>
                  {tag === heute && <span className="mk-heute">heute</span>}
                  {/*
                    „frei" steht nur an Arbeitstagen, die noch kommen. An
                    einem vergangenen Tag ist es keine Auskunft, sondern
                    eine Feststellung über etwas, das niemand mehr ändert –
                    und in einem Monat voller Vergangenheit auch noch Lärm.
                  */}
                  {drin.length === 0 && !istWochenende(tag) && !fremd && tag >= heute && (
                    <span className="mk-frei">frei</span>
                  )}
                </span>
                {drin.map((e) => {
                  const art = artVon(e);
                  const konflikt = doppelt(e, alle).length > 0;
                  const klasse = `mk-eintrag ${konflikt ? 'konflikt' : ''}`;
                  const stil = { background: art.tint, borderLeftColor: art.akz };
                  const titel = `${e.titel} · ${e.zeitraum}${e.adresse ? ` · ${e.adresse}` : ''}`;
                  /*
                    Was und wo – wer montiert, steht in der Auslastung. Im
                    Raster ist der Ort die Auskunft, die den Termin vom
                    nächsten unterscheidet.
                  */
                  const inhalt = (
                    <>
                      <span className="mk-eintrag-kopf">
                        {e.von < tag ? '… ' : ''}
                        {e.kurz || e.titel}
                      </span>
                      {e.adresse && (
                        <span className="mk-eintrag-wo" style={{ color: art.txt }}>
                          {e.adresse}
                        </span>
                      )}
                    </>
                  );
                  const ziel = terminZiel(e, outlookAmPc);
                  /* An der Wand wird nicht geklickt – dort bleibt es Text. */
                  if (gross || !ziel) {
                    return (
                      <span key={e.id} className={klasse} style={stil} title={titel}>
                        {inhalt}
                      </span>
                    );
                  }
                  return (
                    <span key={e.id} className="mk-eintrag-huelle">
                      <a
                        className={`${klasse} mk-eintrag--link`}
                        style={stil}
                        title={`${titel} · ${ziel.amPc ? 'in Outlook am PC öffnen' : 'in Outlook im Browser öffnen'}`}
                        href={ziel.href}
                        {...(ziel.amPc ? {} : { target: '_blank' as const })}
                        rel="noreferrer"
                      >
                        {inhalt}
                      </a>
                      {/*
                        Was Kora eingetragen hat, lässt sich hier auch
                        verschieben und löschen. Der Stift erscheint beim
                        Überfahren – die Tageskarte, in der das früher
                        stand, gibt es nicht mehr.
                      */}
                      {e.quelle === 'kora' && onBearbeiten && (
                        <button
                          type="button"
                          className="mk-stift"
                          title="Verschieben oder löschen"
                          aria-label={`${e.titel} verschieben oder löschen`}
                          onClick={() => onBearbeiten(e)}
                        >
                          <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
                            <path
                              d="M11.5 2.2l2.3 2.3-7.4 7.4-3 .7.7-3 7.4-7.4z"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.4"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
